import { TFunction } from 'i18next';
import { preProcessCurrencyFilter, preProcessMonthsFilter } from '../../filter/FilterRangeEntry';
import { AggregationBucket } from '../../../../models/Aggregations';
import { defaultBucketLabelMapper } from '../../filter/ResponsiveFilterBar';

export const mapReadableUsageConditionFilterLabel = (t: TFunction, bucket: AggregationBucket) => {
    const [lendingModel, loanLimit, termLimit] = bucket.key.split('-');
    const termType = termLimit.endsWith('y') ? 'Year' : 'Month';
    const termLimitCount = parseInt(termLimit.split(' ', 1)[0]);

    if (lendingModel === 'null' && loanLimit === 'null' && termLimit === 'null') {
        return t('filters:usageConditions.noUsageConditions', {
            defaultValue: bucket.value
        });
    } else if (loanLimit === '0' && termLimit === '0') {
        return t('filters:usageConditions.noLoanOrTermLimit', {
            defaultValue: bucket.value,
            lendingModel: lendingModel
        });
    } else if (loanLimit === '0') {
        return t(`filters:usageConditions.mucWithoutLoanLimitTermLimit${termType}`, {
            defaultValue: bucket.value,
            lendingModel: lendingModel,
            count: termLimitCount
        });
    } else if (termLimit === '0') {
        return t('filters:usageConditions.mucWithoutTermLimit', {
            defaultValue: bucket.value,
            lendingModel: lendingModel,
            loanLimit: loanLimit
        });
    } else {
        return t(`filters:usageConditions.mucWithLoanLimitTermLimit${termType}`, {
            defaultValue: bucket.value,
            lendingModel: lendingModel,
            loanLimit: loanLimit,
            count: termLimitCount
        });
    }
};

export const mapReleasedFilterLabel = (t: TFunction, bucket: AggregationBucket): string => {
    if (!bucket.custom) {
        return defaultBucketLabelMapper(t, 'releaseDate', bucket.key, bucket.value);
    }

    if (bucket.customIndex === 1) {
        // filtered by dates
        const fromDate = bucket.key.split('-')[0];
        const toDate = bucket.key.split('-')[1];

        if (toDate.includes('*')) {
            return t('filters:releaseDate.lessThanDate', {
                fromDate
            });
        }

        if (fromDate.includes('*')) {
            return t('filters:releaseDate.moreThanDate', {
                toDate
            });
        }

        return t('filters:releaseDate.fromToDate', {
            fromDate,
            toDate
        });
    }

    // filtered by months

    const fromMonth = bucket.key.split('-')[0];
    const toMonth = bucket.key.split('-')[1];

    if (fromMonth.includes('*')) {
        return t('filters:releaseDate.lessThanMonth', {
            toMonth: preProcessMonthsFilter(toMonth)
        });
    }

    if (toMonth.includes('*')) {
        return t('filters:releaseDate.moreThanMonth', {
            fromMonth: preProcessMonthsFilter(fromMonth)
        });
    }

    return t('filters:releaseDate.fromToMonth', {
        fromMonth: preProcessMonthsFilter(fromMonth),
        toMonth: preProcessMonthsFilter(toMonth)
    });
};

export const mapPagesFilterLabel = (t: TFunction, bucket: AggregationBucket) => {
    if (!bucket.custom) {
        return defaultBucketLabelMapper(t, 'pages', bucket.key, bucket.value);
    }

    const fromPage = bucket.key.split('-')[0];
    const toPage = bucket.key.split('-')[1];

    if (toPage.includes('*') && Number(fromPage) < 1000) {
        return t('filters:pages.fromPage', {
            fromPage
        });
    }

    return t(`filters:pages.${bucket.key}`, {
        defaultValue: t('filters:pages.fromToPage', {
            fromPage,
            toPage
        })
    });
};

export const mapPriceFilterLabel = (t: TFunction, bucket: AggregationBucket) => {
    if (!bucket.custom) {
        return defaultBucketLabelMapper(t, 'mucPrice', bucket.key, bucket.value);
    }

    const fromPrice = bucket.key.split('-')[0];
    const toPrice = bucket.key.split('-')[1];

    if (toPrice && toPrice.includes('*')) {
        return t('filters:mucPrice.fromPrice', {
            fromPrice: preProcessCurrencyFilter(fromPrice)
        });
    }

    return t(`filters:mucPrice.${bucket.key}`, {
        defaultValue: t('filters:mucPrice.fromToPrice', {
            fromPrice: preProcessCurrencyFilter(fromPrice),
            toPrice: preProcessCurrencyFilter(toPrice)
        })
    });
};

export const reservesMapper = (t: TFunction, bucket: Pick<AggregationBucket, 'key' | 'value' | 'custom'>) => {
    if (!bucket.custom) {
        return defaultBucketLabelMapper(t, 'reserves', bucket.key, bucket.value);
    }

    const fromReserves = bucket.key.split('-')[0];
    const toReserves = bucket.key.split('-')[1];

    if (toReserves && toReserves.includes('*')) {
        return t('filters:reserves.fromReserves', {
            fromReserves
        });
    }

    return t('filters:reserves.fromToReserves', {
        fromReserves,
        toReserves
    });
};

export const durationMapper = (t: TFunction, bucket: Pick<AggregationBucket, 'key' | 'value' | 'custom'>) => {
    if (!bucket.custom) {
        return defaultBucketLabelMapper(t, 'duration', bucket.key, bucket.value);
    }

    const fromDuration = bucket.key.split('-')[0];
    const toDuration = bucket.key.split('-')[1];

    if (toDuration && toDuration.includes('*')) {
        return t('filters:duration.fromDuration', {
            fromDuration
        });
    }

    return t('filters:duration.fromToDuration', {
        fromDuration,
        toDuration
    });
};

export const mapCollectionQuickFilter = (t: TFunction, bucket: AggregationBucket): string => {
    return t(`filters:quickfilters.${bucket.key}`, {
        defaultValue: bucket.value
    });
};

export const mapReleaseFrequencyFilterLabel = (t: TFunction, bucket: AggregationBucket): string => {
    const bucketKey = extractReleaseFrequencyKey(bucket.key);
    return t(`filters:releaseFrequency.${bucketKey}`, {
        defaultValue: bucket.value
    });
};

export const mapAuthorCountryFilterLabel = (t: TFunction, bucket: AggregationBucket): string => {
    return t(`countries:countries.${bucket.key}`, {
        defaultValue: t(`filters:authorCountry.${bucket.key}`, {
            defaultValue: bucket.value
        })
    });
};

const extractReleaseFrequencyKey = (key: string): string => {
    switch (key) {
        case 'ONCE':
            return 'once';
        case 'DAILY':
            return 'daily';
        case 'WEEKLY':
            return 'weekly';
        case 'TWICE_A_MONTH':
            return 'twiceAMonth';
        case 'EVERY_TWO_WEEKS':
            return 'everyTwoWeeks';
        case 'MONTHLY':
            return 'monthly';
        case 'EVERY_TWO_MONTHS':
            return 'everyTwoMonths';
        case 'QUARTERLY':
            return 'quarterly';
        case 'ANNUALLY':
            return 'anually';
        case 'TWICE_A_YEAR':
            return 'twiceAYear';
        case 'THREE_TIMES_A_YEAR':
            return 'threeTimesAYear';
        case 'IRREGULARLY':
            return 'irregularly';
        default:
            return key;
    }
};

export const mapFilterGroupKey = (filter: string): string => {
    switch (filter) {
        case 'Quickfilters':
            return 'quickFilters';
        case 'Reserves':
            return 'reserves';
        case 'Format':
            return 'format';
        case 'Age Category':
            return 'ageCategory';
        case 'Age Group':
            return 'ageGroup';
        case 'Genres':
            return 'genres';
        case 'Explicit':
            return 'explicit';
        case 'Series':
            return 'series';
        case 'Authors':
            return 'authors';
        case 'Author Country':
            return 'authorCountry';
        case 'Bestsellers':
            return 'bestsellers';
        case 'Awards':
            return 'awards';
        case 'Award Years':
            return 'awardYears';
        case 'Selections':
            return 'selections';
        case 'Released':
            return 'released';
        case 'Release Frequency':
            return 'releaseFrequency';
        case 'Releases per year':
            return 'releasesPerYear';
        case 'Pages':
            return 'pages';
        case 'Duration':
            return 'duration';
        case 'Production Format':
            return 'productionFormat';
        case 'Illustrated':
            return 'illustrated';
        case 'Publisher':
            return 'publisher';
        case 'Publisher Imprint':
            return 'publisherImprint';
        case 'Lending Model':
            return 'lendingModel';
        case 'Usage Condition (In Collection)':
            return 'usageConditionInCollection';
        case 'Usage Condition (Shop)':
            return 'usageConditionShop';
        case 'Language':
            return 'language';
        case 'Price':
            return 'price';
        case 'Content Provider':
            return 'contentProvider';
        case 'Product Format':
            return 'productFormat';
        case 'Selected Lending Model':
            return 'selectedLendingModel';
        case 'Selected Price':
            return 'selectedPrice';
        case 'Selected Usage Condition':
            return 'selectedUsageCondition';
        case 'Narrators':
            return 'narrators';
        default:
            return filter;
    }
};
